<script setup>
import router from '@/router';
import {onMounted, ref} from 'vue';
import ClientReportService from '@/services/ClientReportService';
import {useRoute} from 'vue-router';
import LineChart from '@/components/echarts/LineChart.vue';
import PieChart from '@/components/echarts/PieChart.vue';
import {formatDate, formatAmount, formatNumber, formatPercents} from '../helper';
import {useI18n} from 'vue-i18n';
import InstructionsTableComponent from '@/components/instructions/InstructionsTableComponent.vue';
import {createConfirmDialog} from 'vuejs-confirm-dialog';
import DateRangeModal from '@/components/modals/DateRangeModal.vue';

const {t} = useI18n();
const route = useRoute();
const contract = ref(null);
const chartData = ref(null);
const chartCategories = ref(null);
const transactions = ref(null);
const trades = ref(null);
const strategies = ref(null);
const rangeStats = ref(null);
const pendingDeposits = ref([]);
const dateRange = ref({from: null, to: null});
const composition = ref(null);
const instructions = ref([]);
const showInstructions = ref(false);
const loading = ref({
  contract: true,
  strategies: true,
  contract_stats: true,
  contract_history: true,
  contract_composition: true,
  contract_transactions: true,
  contract_trades: true,
  contract_instructions: true,
});

function navigateTo(to) {
  router.push(to);
}

function getCurrentYearYtdRange() {
  const today = new Date();
  today.setDate(today.getDate() - 1); // včerejšek
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return {
    from: `${year}-01-01`,
    to: `${year}-${month}-${day}`,
  };
}

function openDateRangeModal() {
  const {reveal, onConfirm} = createConfirmDialog(DateRangeModal, {
    fromDate: dateRange.value.from,
    toDate: dateRange.value.to,
    // datum první investice smlouvy/strategie - modal nedovolí vybrat dřívější začátek období
    minDate: rangeStats.value?.first_position_date ?? null,
  });
  reveal();
  onConfirm(async (data) => {
    dateRange.value = data;

    const rangeToUse = (data.from || data.to) ? data : getCurrentYearYtdRange();
    const rangeData = await ClientReportService.getContractStats(route.params.id, rangeToUse);
    rangeStats.value = rangeData.data;
  });
}

onMounted(async () => {
  // YTD range se načítá přímo v Promise.all, aby nedošlo k race condition:
  // dřívější verze spouštěla paralelně all-time request (bez from/to) i YTD request,
  // přičemž all-time response přepisovala rangeStats na špatný počáteční stav (datum podpisu smlouvy).
  Promise.all([
    ClientReportService.getContract(route.params.id),
    ClientReportService.getStrategies(route.params.id),
    ClientReportService.getContractStats(route.params.id, getCurrentYearYtdRange()),
  ]).then(([contractData, strtgs, ytdStats]) => {
    contract.value = contractData.data;
    strategies.value = strtgs.data;
    rangeStats.value = ytdStats.data;
    pendingDeposits.value = ytdStats.data.pending_deposits;
    showInstructions.value = contract.value.meta?.has_instructions || false;
    if (showInstructions.value) {
      getContractInstructions();
    }
    loading.value = {
      ...loading.value,
      contract: false,
      strategies: false,
      contract_stats: false,
    };
  });

  ClientReportService.getContractHistory(route.params.id)
      .then((portfolioHistory) => {
        chartData.value = portfolioHistory.data.map(candle => candle.amount);
        chartCategories.value = portfolioHistory.data.map(candle => candle.date);
        loading.value = {
          ...loading.value,
          contract_history: false,
        };
      });

  Promise.all([
    ClientReportService.getContractComposition(route.params.id),
    ClientReportService.getTransactions(route.params.id),
    ClientReportService.getTrades(route.params.id)
  ]).then(([cmpst, trans, trds]) => {
    transactions.value = trans.data;
    trades.value = trds.data;
    composition.value = cmpst.data;
    loading.value = {
      ...loading.value,
      contract_composition: false,
      contract_transactions: false,
      contract_trades: false,
    };
  });
});

function getContractInstructions() {
  if (showInstructions.value) {
    ClientReportService.getContractInstructions(route.params.id).then((response) => {
      instructions.value = response.data;
      loading.value = {
        ...loading.value,
        contract_instructions: false,
      }
    });
  }
}
</script>

<template>

  <div class="page-section">
    <div class="page-section__heading">
      <div class="row">
        <div class="col-2">
          <a @click="router.back()" class="page-section__heading__back"><i class="mdi mdi-arrow-left"></i> {{ t('contractOverview.back') }}</a>
        </div>
        <div class="col-8 text-center">
          <div v-if="loading.contract" class="d-block">
            <Skeletor width="150" height="22" class="d-inline-block" pill /><br/>
            <Skeletor width="150" height="10" class="mt-2 d-inline-block" pill />
          </div>
          <div v-else>
            <div class="page-section__title">
              {{ contract?.contractNumber }}
            </div>
            <div class="page-section__subtitle">
              {{ contract?.contractType }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="page-section__content">

      <div v-if="loading.strategies">
        <div class="row">
          <div class="col-12 col-lg-6" v-for="i in 2" :key="i">
            <Skeletor width="100%" height="56" class="mb-3" pill />
          </div>
        </div>
      </div>
      <div class="row justify-content-start" v-else>
        <div v-for="contractStrategy of strategies" v-bind:key="contractStrategy" class="col-12 col-lg-6 mb-2 mb-lg-3">
          <div @click="router.push('/contract-strategy/' + contractStrategy.id)" class="card cursor-pointer card-contract">
            <div class="card-header card-header--small">
              <div class="row align-items-center">
                <div class="col-auto">
                  <div class="card-header__title">{{ contractStrategy.label }}</div>
                  <div class="card-header__subtitle">{{ contractStrategy.strategy.name }}</div>
                </div>
                <div class="col text-end">
                  <div class="card-header__value">
                    <div class="">{{ formatAmount(contractStrategy.performance.totalAmount, contractStrategy.performance.currency) }}</div>
                    <div class="card-header__value__sub" :class="{'ci ci--up': contractStrategy.performance.performancePerc > 0, 'ci ci--down': contractStrategy.performance.performancePerc < 0}">
                      <span>{{ contractStrategy.performance.performancePerc > 0 ? '+' : '' }}</span>{{ formatPercents(contractStrategy.performance.performancePerc) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      <div class="row">
        <div class="col-md-6 mt-4 col-12">
          <div v-if="loading.contract_stats" class="text-center mt-4">
            <div class="row mb-3">
              <div class="col-12 text-center">
                <div class="row d-none d-md-flex">
                  <div class="col-md-6 text-center" v-for="i in 2" :key="i">
                    <Skeletor width="100%" height="32" pill />
                    <Skeletor width="100%" height="22" pill class="mt-2" />
                  </div>
                </div>
                <div class="row d-md-none">
                  <div class="col-12">
                    <Skeletor width="100%" height="32" pill />
                    <Skeletor width="100%" height="22" pill class="mt-2" />
                  </div>
                </div>
              </div>
            </div>
            <div class="row mt-4">
              <div class="col-12">
                 <Skeletor width="100%" height="150" />
              </div>
            </div>
          </div>
          <div v-if="!loading.contract_stats && rangeStats">
            <div class="row g-2 g-md-3 mb-3">
              <!-- Hodnota smlouvy -->
              <div class="col-12 col-md-6 col-xxl-3">
                <div class="card card--flat card--big-value h-100">
                  <div class="card-body py-0 d-flex flex-column justify-content-center">
                    <span class="card__value__label">{{ t('contractOverview.value') }}</span>
                    <div class="card__value">
                      {{ formatAmount(contract.performance.totalAmount, contract.performance.currency) }}
                    </div>
                  </div>
                </div>
              </div>
              <!-- Investováno — celková suma vložených prostředků -->
              <div class="col-12 col-md-6 col-xxl-3">
                <div class="card card--flat card--big-value h-100">
                  <div class="card-body py-0 d-flex flex-column justify-content-center">
                    <span class="card__value__label">{{ t('dashboardClient.invested') }}</span>
                    <div class="card__value">
                      {{ formatAmount(contract.performance.investedAmount, contract.performance.currency) }}
                    </div>
                  </div>
                </div>
              </div>
              <!-- Výnos za celé období -->
              <div class="col-12 col-md-6 col-xxl-3">
                <div class="card card--flat card--big-value h-100">
                  <div class="card-body py-0 d-flex flex-column justify-content-center">
                    <span class="card__value__label">{{ t('dashboardClient.all_period') }}</span>
                    <div class="card__value" :class="{'ci ci--up': contract.performance.performancePerc > 0, 'ci ci--down': contract.performance.performancePerc < 0}">
                      <span>{{ contract.performance.gain > 0 ? '+' : '' }}</span>{{ formatAmount(contract.performance.gain, contract.performance.currency) }}
                    </div>
                    <div class="card__subvalue" :class="{'ci ci--up': contract.performance.performancePerc > 0, 'ci ci--down': contract.performance.performancePerc < 0}">
                      <span>{{ contract.performance.performancePerc > 0 ? '+' : '' }}</span>{{ formatPercents(contract.performance.performancePerc) }}
                    </div>
                  </div>
                </div>
              </div>
              <!-- Date range karta — výběr vlastního období, výchozí YTD -->
              <div class="col-12 col-md-6 col-xxl-3">
                <div class="card card--flat card--big-value h-100 cursor-pointer" @click="openDateRangeModal">
                  <div class="card-body py-0 d-flex flex-column justify-content-center">
                    <span class="card__value__label">
                      <!-- období z API (rangeStats.range) má přednost - backend posouvá začátek na první investici -->
                      {{ rangeStats?.range ? (rangeStats.range.from ? formatDate(rangeStats.range.from, 'd.m.Y') : '...') + ' - ' + (rangeStats.range.to ? formatDate(rangeStats.range.to, 'd.m.Y') : '...') : dateRange.from || dateRange.to ? (dateRange.from ? formatDate(dateRange.from, 'd.m.Y') : '...') + ' - ' + (dateRange.to ? formatDate(dateRange.to, 'd.m.Y') : '...') : formatDate(getCurrentYearYtdRange().from, 'd.m.Y') + ' - ' + formatDate(getCurrentYearYtdRange().to, 'd.m.Y') }}
                      <i class="mdi mdi-pencil ms-1"></i>
                    </span>
                    <div class="card__value" :class="{'ci ci--up': rangeStats.performance.performancePerc > 0, 'ci ci--down': rangeStats.performance.performancePerc < 0}">
                      <span>{{ rangeStats.performance.gain > 0 ? '+' : '' }}</span>{{ formatAmount(rangeStats.performance.gain, rangeStats.performance.currency) }}
                    </div>
                    <div class="card__subvalue" :class="{'ci ci--up': rangeStats.performance.performancePerc > 0, 'ci ci--down': rangeStats.performance.performancePerc < 0}">
                      <span>{{ rangeStats.performance.performancePerc > 0 ? '+' : '' }}</span>{{ formatPercents(rangeStats.performance.performancePerc) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row mt-5" v-if="pendingDeposits.length > 0">
                <div class="col-12">
                  <div v-for="deposit of pendingDeposits" :key="deposit.id" class="alert alert-info">
                                      <div class="alert-icon">
                    <i class="mdi mdi-cash-multiple"></i>
                  </div>
                    <div class="alert-content" v-html="t('pendingDeposits.info_box_text_contract', {label_strategy: deposit.label_strategy ?? '', amount_formatted: formatAmount(deposit.amount, deposit.currency)})">
                    </div>
                  </div>
                </div>
              </div>



            <div class="row mt-4">
              <div class="col-12">
                <div class="card card--flat">
                  <div class="card-body card-body--only-chart">
                    <div v-if="chartData" class="card-body__chart">
                      <LineChart
                          :series="[
                          {
                            name: t('contractOverview.value_portfolio'),
                            data: chartData,
                          }
                        ]"
                          :valuesType="'amount'"
                          :currency="contract.currency"
                          :categories="chartCategories"
                      ></LineChart>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6 col-12 mt-4">
          <div class="row">
            <div class="col-12">
              <div v-if="loading.contract">
                <Skeletor width="100%" height="240" />
              </div>
              <div v-if="!loading.contract" class="card card--no-header h-100">
                <div class="card-body">
                  <div class="info-table">
                    <div class="info-table__row row">
                      <div class="info-table__label col-12">
                        {{ t('contractOverview.contract_type') }}
                      </div>
                      <div class="info-table__value col-12">
                        {{ contract.contractType }}
                      </div>
                    </div>
                    <div class="info-table__row row">
                      <div class="info-table__label col-12">
                        {{ t('contractOverview.contract_number') }}
                      </div>
                      <div class="info-table__value col-12">
                        {{ contract.contractNumber }}
                      </div>
                    </div>
                    <div class="info-table__row row">
                      <div class="info-table__label col-12">
                        {{ t('contractOverview.contract_created_at') }}
                      </div>
                      <div class="info-table__value col-12">
                        {{ formatDate(contract.signed, 'd.m.Y') }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-2 mt-lg-3">
            <!--            <div class="col-6">
                          <router-link class="btn btn-primary w-100" to="/service-requirements">Vložit prostředky</router-link>
                        </div>-->
            <div class="col-12">
              <router-link class="btn btn-primary w-100" to="/service-requirements/create">{{ t('contractOverview.service_request') }}</router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="page-section page-section--tabs mt-4">
    <div class="page-section__content">

      <ul class="nav--tabs nav nav--pills" id="pills-tab" role="tablist">
        <li class="nav-item" :class="showInstructions ? 'col-3' : 'col-4'" role="presentation">
          <button class="nav-link active" id="contract-tab-composition" data-bs-toggle="pill" data-bs-target="#contract-tab-composition-content" type="button" role="tab" aria-controls="contract-tab-composition-content" aria-selected="true">{{ t('contractOverview.composition') }}</button>
        </li>
        <li class="nav-item" :class="showInstructions ? 'col-3' : 'col-4'" role="presentation">
          <button class="nav-link" id="contract-tab-trades" data-bs-toggle="pill" data-bs-target="#contract-tab-trades-content" type="button" role="tab" aria-controls="contract-tab-trades-content" aria-selected="true">{{ t('contractOverview.trades') }}</button>
        </li>
        <li class="nav-item" :class="showInstructions ? 'col-3' : 'col-4'" role="presentation">
          <button class="nav-link" id="contract-tab-transactions" data-bs-toggle="pill" data-bs-target="#contract-tab-transactions-content" type="button" role="tab" aria-controls="contract-tab-transactions-content" aria-selected="false">{{ t('contractOverview.transactions') }}</button>
        </li>
        <li class="nav-item col-3" v-if="showInstructions" role="presentation">
          <button class="nav-link" id="contract-tab-instructions" data-bs-toggle="pill" data-bs-target="#contract-tab-instructions-content" type="button" role="tab" aria-controls="contract-tab-instructions-content" aria-selected="false">{{ t('contractOverview.instructions') }}</button>
        </li>
      </ul>
      <div class="tab-content" id="pills-tabContent">
        <div class="tab-pane fade show active" id="contract-tab-composition-content" role="tabpanel" aria-labelledby="contract-tab-composition-content-tab">
          <div class="card">
            <div class="card-body card-body--only-chart" v-if="composition">
              <div class="card-body__chart card-body__chart--hauto">
                <PieChart
                    :view="'withTable'"
                    :loading="loading.contract_composition"
                    :series="[
                          {
                            name: t('contractOverview.composition_of_portfolio'),
                            data: composition
                          }
                        ]"
                ></PieChart>
              </div>
            </div>
          </div>
        </div>
        <div class="tab-pane fade" id="contract-tab-trades-content" role="tabpanel" aria-labelledby="contract-tab-trades-content-tab">

          <div v-if="loading.contract_trades" class="text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ t('contractOverview.loading') }}</span>
            </div>
          </div>
          <div v-if="!loading.contract_trades">
            <div class="card shadow">
              <div class="card-body">
                <div class="table-container">
                  <table class="table table--last-to-right table--cells-py-0">
                    <thead>
                      <tr>
                        <th scope="col">
                          {{ t('contractOverview.date') }}
                          <div class="table__sub-value">{{ t('contractOverview.reference_number') }}</div>
                        </th>
                        <th scope="col">
                          {{ t('contractOverview.type') }}
                          <div class="table__sub-value">{{ t('contractOverview.isin') }}</div>
                        </th>
                        <th scope="col">
                          {{ t('contractOverview.strategy') }}
                          <div class="table__sub-value">{{ t('contractOverview.amount') }} | {{ t('contractOverview.quantity') }} | {{ t('contractOverview.price') }}</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="trade of trades" :key="trade">
                        <td>
                          {{ formatDate(trade.transaction_date, 'd.m.Y H:i') }}
                          <div class="table__sub-value">{{ trade.trans_ref }}</div>
                        </td>
                        <td>
                          {{ trade.type?.sec_transaction_type }} - {{ trade.sec_account?.equity_name }}
                          <div v-if="trade.sec_account" class="table__sub-value">{{ trade.sec_account?.isin }}</div>
                        </td>
                        <td>
                          {{ trade.contract_strategy?.strategy?.name }}
                          <div class="table__sub-value">{{ formatAmount(trade.amount, trade.currency, 2, true) }} | {{ formatNumber(trade.quantity, 5, true) }} ks | {{ formatAmount(trade.amount/trade.quantity, trade.currency, 2, true) }} / ks</div>
                        </td>
                      </tr>
                      <tr v-if="!trades.length">
                        <td colspan="3" class="table__empty-td">
                          <div class="table__empty-text">
                            {{ t('contractOverview.no_trades_yet') }}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

        </div>
        <div class="tab-pane fade" id="contract-tab-transactions-content" role="tabpanel" aria-labelledby="contract-tab-transactions">

          <div v-if="loading.contract_transactions" class="text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ t('contractOverview.loading') }}</span>
            </div>
          </div>
          <div v-if="!loading.contract_transactions">
            <div class="card shadow">
              <div class="card-body">
                <div class="table-container">
                  <table class="table table--last-to-right table--cells-py-0">
                    <thead>
                      <tr>
                        <th scope="col">
                          {{ t('contractOverview.date') }}
                          <div class="table__sub-value">{{ t('contractOverview.reference_number') }}</div>
                        </th>
                        <th scope="col">
                          {{ t('contractOverview.type') }}
                        </th>
                        <th scope="col">
                          {{ t('contractOverview.strategy') }}
                          <div class="table__sub-value">{{ t('contractOverview.amount') }}</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="transaction of transactions" :key="transaction">
                        <td>
                          {{ formatDate(transaction.transaction_date, 'd.m.Y H:i') }}
                          <div class="table__sub-value">{{ transaction.trans_ref }}</div>
                        </td>
                        <td>
                          {{ transaction.type?.cash_transaction_type }}
                          <div class="table__sub-value">{{ transaction.fee_type?.fee_type_name }}</div>
                        </td>
                        <td>
                          {{ transaction.contract_strategy?.strategy?.name }}
                          <div class="table__sub-value">{{ formatAmount(transaction.amount, transaction.currency) }}</div>
                        </td>
                      </tr>
                      <tr v-if="!transactions.length">
                        <td colspan="3" class="table__empty-td">
                          <div class="table__empty-text">
                            {{ t('contractOverview.no_transactions_yet') }}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="tab-pane fade" id="contract-tab-instructions-content" role="tabpanel" aria-labelledby="contract-tab-instructions-content-tab">
          <div v-if="loading.contract_instructions" class="text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ t('contractOverview.loading') }}</span>
            </div>
          </div>
          <div v-if="!loading.contract_instructions">
            <div class="card shadow">
              <div class="card-body">
                <InstructionsTableComponent :instructions="instructions"></InstructionsTableComponent>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>
